import React, { useEffect, useState } from "react";
import { ButtonGroup, Spinner, ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import gettext from "../../shared/utils/gettext";
import cropr from "../../shared/utils/cropr";
import moment from "moment";
import { js_settings } from "../../shared/utils/legacy";
import { Bar, BarChart, Cell, Tooltip, XAxis, YAxis } from "recharts";

function ElevationView(props) {
  const { cropfield, setImageLocation } = props;
  const centroid = cropfield && cropfield.getCentroid();
  const x = centroid && centroid.x;
  const y = centroid && centroid.y;
  const [statistics, setStatistics] = useState();
  const [shadowStatistics, setShadowStatistics] = useState();
  const [statisticIndex, setStatisticIndex] = useState();
  const [statisticChoices, setStatisticChoices] = useState();
  const [status, setStatus] = useState("loading");
  const [drainage, setDrainage] = useState(false);
  const [accumulation, setAccumulation] = useState(false);
  const [shadowmap, setShadowmap] = useState(false);
  const [hasShadowmap, setHasShadowmap] = useState(false);
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  useEffect(() => {
    setImageLocation(null);
    return function reset() {
      setImageLocation(null);
    };
  }, [setImageLocation]);
  useEffect(() => {
    if (!x) {
      setStatisticChoices(null);
      setHasShadowmap(false);
      return;
    }
    setStatus("loading");
    const url = `${js_settings.REFERENCE_DOMAIN}/height/statistics/?p=${y},${x}`;
    fetch(url, {
      headers: js_settings.JWT_REFERENCE,
    }).then((response) => {
      if (response.ok) {
        response.json().then((result) => {
          const choices = result.versions.map((h, i) => {
            return { idx: i + 1, date: h.date };
          });
          setStatisticChoices(choices);
          setHasShadowmap(!!result.versions.find((v) => !!v.dsm_layer));
          if (
            choices.length > 0 &&
            (!statisticIndex || choices.length < parseInt(statisticIndex))
          ) {
            setStatisticIndex(4); // AHN4 is the last complete one and therefore the best starting point
          }
        });
      } else {
        setStatisticChoices(null);
        setHasShadowmap(false);
        setStatisticIndex(null);
        setStatus("nodata");
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [x, y]);
  useEffect(() => {
    const wkt = cropfield && cropfield.get("geometry");
    if (!statisticIndex || !wkt) {
      return;
    }
    setStatisticsLoading(true);
    const url = `${js_settings.REFERENCE_DOMAIN}/height/statistics/${statisticIndex}`;
    fetch(url, {
      method: "POST",
      body: JSON.stringify({
        polygon: wkt,
        srid: 4326,
      }),
      headers: js_settings.JWT_REFERENCE,
    }).then((response) => {
      setStatisticsLoading(false);
      if (response.ok) {
        response.json().then((result) => {
          setStatistics(result);
        });
      } else {
        setStatistics(null);
      }
    });
  }, [cropfield, statisticIndex, setStatistics]);
  useEffect(() => {
    setShadowStatistics(null);
    const wkt = cropfield && cropfield.get("geometry");
    if (!wkt || !shadowmap) {
      return;
    }
    const url = `${js_settings.REFERENCE_DOMAIN}/height/shadow/`;
    fetch(url, {
      method: "POST",
      body: JSON.stringify({
        polygon: wkt,
        srid: 4326,
      }),
      headers: js_settings.JWT_REFERENCE,
    }).then((response) => {
      if (response.ok) {
        response.json().then((result) => {
          setShadowStatistics(result);
        });
      } else {
        setShadowStatistics(null);
      }
    });
  }, [cropfield, setShadowStatistics, shadowmap]);
  useEffect(() => {
    const stats = shadowmap ? shadowStatistics : statistics;
    if (!stats) {
      setImageLocation(null);
      return;
    }
    const geom = cropfield.parseGeometry();
    geom.geometry.calculateBounds();
    const imageLocation = {
      type: "image",
      url: `${js_settings.REFERENCE_DOMAIN}/height/img/${stats.img_key}/${
        drainage ? "?type=drainage" : accumulation ? "?type=accumulation" : ""
      }`,
      bounds: [
        geom.geometry.bounds.left,
        geom.geometry.bounds.bottom,
        geom.geometry.bounds.right,
        geom.geometry.bounds.top,
      ],
      zoomTo: true,
      opacity: 1,
      key: stats.img_key,
      name: shadowmap ? gettext("Shadow") : gettext("Height"),
      display_name: shadowmap ? gettext("Shadow") : gettext("Height") + " (m)",
      measurement_id: shadowmap ? "shadow" : `height${statisticIndex}`,
      date: moment(statisticChoices[statisticIndex - 1].date).format("YYYYMMDD"),
      data_type: "elevation",
      legend: stats.histogram
        .slice()
        .reverse()
        .map((h) => {
          return {
            c: h.color,
            count: h.percentage,
            name:
              shadowmap &&
              {
                1: gettext("No shadow"),
                2: gettext("Little shadow"),
                3: gettext("Some shadow"),
                4: gettext("Much shadow"),
              }[h.min],
            min: shadowmap ? h.min : h.height,
          };
        }),
      cropfield: cropfield,
    };
    if (shadowmap) {
      imageLocation.mappings = {
        [imageLocation.name]: {
          colors: imageLocation.legend.reduce((obj, l) => ({ ...obj, [l.name]: l.c }), {}),
          values: imageLocation.legend.reduce((obj, l) => ({ ...obj, [l.min]: l.name }), {}),
        },
      };
    }
    setImageLocation(imageLocation);
    setStatus("loaded");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setImageLocation, statistics, drainage, accumulation, shadowStatistics]);
  if (status === "loading") {
    return (
      <div className="text-center mt-5 mb-5">
        <Spinner animation="border" role="status">
          <span className="sr-only">Loading...</span>
        </Spinner>
      </div>
    );
  }

  const hasData = status === "loaded" && statistics?.statistics;

  return (
    <div className="bg-white p-1" style={{ minWidth: 460 }}>
      <div style={{ minHeight: 340 }}>
        {hasData && !statisticsLoading ? (
          <>
            <div className="p-1">
              {gettext("min")}:&nbsp; {cropr.toLocaleFixed(statistics.statistics.dem_min, 2)},&nbsp;
              {gettext("max")}:&nbsp;
              {cropr.toLocaleFixed(statistics.statistics.dem_max, 2)},&nbsp;{gettext("avg")}:&nbsp;
              {cropr.toLocaleFixed(statistics.statistics.dem_avg, 2)},&nbsp;{gettext("diff")}:&nbsp;
              {cropr.toLocaleFixed(
                statistics.statistics.dem_max - statistics.statistics.dem_min,
                2
              )}
            </div>
            <BarChart width={450} height={300} data={statistics.histogram}>
              <XAxis dataKey="height" />
              <YAxis />
              <Tooltip
                labelFormatter={(e) => `${gettext("Height")}: ${e}m`}
                formatter={(value) => `${value}%`}
                contentStyle={{ backgroundColor: "rgba(255,255,255,.9)" }}
              />
              <Bar dataKey="percentage" name={gettext("Percentage")}>
                {statistics.histogram.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </>
        ) : statisticsLoading ? (
          <div
            className="text-center d-flex align-items-center justify-content-center"
            style={{ height: 340 }}
          >
            <Spinner animation="border" role="status">
              <span className="sr-only">Loading...</span>
            </Spinner>
          </div>
        ) : (
          <div className="alert alert-info pt-2 mt-2 mb-2">
            {gettext("No elevation data is available for this location")}
          </div>
        )}
      </div>
      {statisticChoices && statisticChoices.length > 0 && (
        <div className="pb-2">
          <ToggleButtonGroup
            size="sm"
            value={statisticIndex}
            name="index"
            onChange={setStatisticIndex}
          >
            {statisticChoices.map((sc) => (
              <ToggleButton
                value={sc.idx}
                name="index"
                variant={statisticIndex === sc.idx ? "primary" : "secondary"}
                key={sc.idx + sc.date}
              >
                {moment(sc.date).format("YYYY")}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {hasData && (
            <>
              <ButtonGroup toggle className="pl-1" size="sm">
                <ToggleButton
                  type="checkbox"
                  variant={drainage ? "primary" : "secondary"}
                  checked={drainage}
                  value={drainage}
                  onChange={(e) => {
                    setDrainage(e.currentTarget.checked);
                    setAccumulation(false);
                    setShadowmap(false);
                  }}
                  title={
                    gettext("Show drainage lines.") +
                    " " +
                    gettext(
                      "This shows the spots of highest water accumulation indicated in black."
                    )
                  }
                >
                  &nbsp;<i className="far fa-water-lower"> </i>&nbsp;
                </ToggleButton>
              </ButtonGroup>
              <ButtonGroup toggle className="pl-1" size="sm">
                <ToggleButton
                  type="checkbox"
                  variant={accumulation ? "primary" : "secondary"}
                  checked={accumulation}
                  value={accumulation}
                  onChange={(e) => {
                    setDrainage(false);
                    setAccumulation(e.currentTarget.checked);
                    setShadowmap(false);
                  }}
                  title={gettext(
                    "Show water accumulation. This is a visualisation of where the water accumulates if the soil is saturated (or if you would view the soil as a hard surface, and the water would flow to the nearest lower spot). "
                  )}
                >
                  &nbsp;<i className="far fa-random"> </i>&nbsp;
                </ToggleButton>
              </ButtonGroup>
              {hasShadowmap && (
                <ButtonGroup toggle className="pl-1" size="sm">
                  <ToggleButton
                    type="checkbox"
                    variant={shadowmap ? "primary" : "secondary"}
                    checked={shadowmap}
                    value={shadowmap}
                    onChange={(e) => {
                      setDrainage(false);
                      setAccumulation(false);
                      setShadowmap(e.currentTarget.checked);
                    }}
                    title={
                      gettext("Shadow map") +
                      ". " +
                      // prettier-ignore
                      gettext("Shows areas where sun radiation is less on average due to shadows from trees bordering the field.") +
                      " " +
                      gettext("Only shadows in the period may - august are taken into account.") +
                      " " +
                      gettext("Actual shadows may deviate due to changes in the landscape.")
                    }
                  >
                    &nbsp;
                    <i
                      className={
                        shadowmap && !shadowStatistics
                          ? "far fa-circle-notch fa-spin"
                          : "far fa-trees"
                      }
                    >
                      {" "}
                    </i>
                    &nbsp;
                  </ToggleButton>
                </ButtonGroup>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default ElevationView;
